import type { RankedSpace } from "../types";

/**
 * A photo in the curation UI: a URL with whether it is selected for the proposal.
 * The position in the list IS the display order.
 */
export interface PhotoItem {
  url: string;
  selected: boolean;
}

/**
 * Build the ordered, selectable list shown in the curation step for one section
 * (room or space) of a space.
 *
 * - `fullSet`: every available photo (the source of truth that survives edits).
 * - `curated`: the previously saved ordered subset (`selectedImages` /
 *   `selectedSpacePhotos`), or `undefined` when nothing was curated yet.
 *
 * When not curated yet → all photos selected, in their original order (opt-out).
 * When curated → selected photos first (in saved order), then the remaining
 * photos as deselected (preserving original order), so removed photos can be
 * re-added when editing.
 */
export function buildPhotoItems(
  fullSet: string[],
  curated: string[] | undefined,
): PhotoItem[] {
  if (curated == null) {
    return fullSet.map((url) => ({ url, selected: true }));
  }
  const fullSetMembers = new Set(fullSet);
  const curatedMembers = new Set(curated);
  const selected: PhotoItem[] = curated
    .filter((url) => fullSetMembers.has(url))
    .map((url) => ({ url, selected: true }));
  const deselected: PhotoItem[] = fullSet
    .filter((url) => !curatedMembers.has(url))
    .map((url) => ({ url, selected: false }));
  return [...selected, ...deselected];
}

/** Serialize curation UI items back into the persisted ordered subset. */
export function toCuratedUrls(items: PhotoItem[]): string[] {
  return items.filter((item) => item.selected).map((item) => item.url);
}

/**
 * Curated room photos for display: the saved subset, or all room images when
 * the space was never curated.
 */
export function curatedRoomPhotos(space: RankedSpace): string[] {
  return space.selectedImages ?? space.images ?? [];
}

/**
 * Curated space (building) photos for display: the saved subset, or all space
 * photos when never curated.
 */
export function curatedSpacePhotos(space: RankedSpace): string[] {
  return space.selectedSpacePhotos ?? space.spacePhotos ?? [];
}
